/**
 * P0.5.13A — Athlete Match Exposure Section
 *
 * Consumes canonical endpoints (NO frontend formula duplication):
 *   GET /reports/match-exposure/athlete/{athlete_id}?date_from&date_to
 *   GET /reports/match-exposure/athlete/{athlete_id}/timeseries?date_from&date_to
 *
 * Semantics (STRICT):
 *   • match_minutes = null  → display "—"   (missing)
 *   • match_minutes = 0     → display "0"   (explicit)
 *   • NEVER convert null → 0
 *   • NEVER compute canonical KPIs in React
 *   • Chart Competitive/Friendly filter never mutates summary KPIs
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, Clock, Activity, Users, Loader2, Info } from 'lucide-react';

/* ─── helpers ─────────────────────────────────────────────────── */
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const fmtNum = (v, suffix = '') => (isNum(v) ? `${v}${suffix}` : '—');
const fmtPct = (v) => (isNum(v) ? `${v}%` : '—');

const tooltipStyle = {
  backgroundColor: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '6px',
  fontSize: '11px',
  color: '#e2e8f0',
};

/* ─── KPI card (matches host-page style) ─────────────────────── */
const KpiCard = ({ label, value, hint, testid, accent = 'primary' }) => (
  <Card
    className="bg-zinc-900 border-zinc-800 p-4"
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
      {label}
    </div>
    <div
      className={`text-2xl font-bold ${
        accent === 'muted' ? 'text-zinc-300' : 'text-cyan-300'
      }`}
    >
      {value}
    </div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

/* ─── event_type filter (client-side chart only) ────────────── */
const EventTypeTabs = ({ value, onChange, t }) => {
  const opts = [
    { v: 'ALL', label: t('All', 'Tutte') },
    { v: 'MATCH', label: t('Competitive', 'Competizione') },
    { v: 'FRIENDLY_MATCH', label: t('Friendly', 'Amichevoli') },
  ];
  return (
    <div className="flex items-center gap-1" role="tablist" aria-label={t('Match type filter', 'Filtro tipo partita')}>
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          role="tab"
          aria-selected={value === o.v}
          onClick={() => onChange(o.v)}
          data-testid={`ath-me-filter-${o.v}`}
          className={`px-2.5 py-1 rounded text-[11px] font-medium border transition-colors min-h-[28px] ${
            value === o.v
              ? 'bg-cyan-600 border-cyan-500 text-white'
              : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

/* ─── Chart selector tabs ───────────────────────────────────── */
const ChartTabs = ({ tabs, value, onChange }) => (
  <div
    className="flex flex-wrap items-center gap-1 border-b border-zinc-800 mb-3"
    role="tablist"
    aria-label="chart selector"
  >
    {tabs.map((tab) => (
      <button
        key={tab.v}
        type="button"
        role="tab"
        aria-selected={value === tab.v}
        onClick={() => onChange(tab.v)}
        data-testid={`ath-me-chart-${tab.v}`}
        className={`px-3 py-2 text-xs font-medium border-b-2 min-h-[36px] transition-colors ${
          value === tab.v
            ? 'border-cyan-500 text-cyan-300'
            : 'border-transparent text-zinc-500 hover:text-zinc-300'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

/* ─── Custom tooltip for Minutes/Playing-time ───────────────── */
const AthleteMatchTooltip = ({ active, payload, t }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  const typeLabel = p.event_type === 'FRIENDLY_MATCH'
    ? t('Friendly', 'Amichevole')
    : t('Competitive', 'Competizione');
  return (
    <div style={tooltipStyle} className="p-2 space-y-0.5">
      <div className="text-zinc-100 font-semibold">{p.title || p.date}</div>
      <div className="text-zinc-400">
        {p.date} · <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-300">{typeLabel}</Badge>
      </div>
      <div className="text-zinc-300">
        {t('Minutes Played', 'Minuti giocati')}:{' '}
        <b className="text-cyan-300">
          {p.data_status === 'MISSING_MATCH_MINUTES' || p.data_status === 'NO_ROW'
            ? t('Minutes not entered', 'Minuti non inseriti')
            : fmtNum(p.match_minutes)}
        </b>
      </div>
      <div className="text-zinc-300">
        {t('Available Minutes', 'Minuti disponibili')}: <b className="text-zinc-200">{fmtNum(p.match_available_minutes)}</b>
      </div>
      <div className="text-zinc-300">
        {t('Playing Time', 'Playing Time')}: <b className="text-zinc-200">{fmtPct(p.playing_time_pct)}</b>
      </div>
      <div className="text-zinc-300">
        {t('Post-Match Minutes', 'Minuti post partita')}: <b className="text-zinc-200">{fmtNum(p.post_match_minutes)}</b>
      </div>
      <div className="text-zinc-300">
        {t('RPE', 'RPE')}: <b className="text-zinc-200">{fmtNum(p.rpe)}</b> ·{' '}
        {t('Match-Day Load', 'Match-Day Load')}: <b className="text-zinc-200">{isNum(p.training_load) ? `${p.training_load} AU` : '—'}</b>
      </div>
    </div>
  );
};

/* ─── Main component ────────────────────────────────────────── */
const AthleteMatchExposureSection = ({ api, athleteId, teamId, dateFrom, dateTo, t }) => {
  const [aggregate, setAggregate] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chart, setChart] = useState('minutes'); // minutes|playing|rpe|load
  const [typeFilter, setTypeFilter] = useState('ALL');

  useEffect(() => {
    if (!athleteId || !dateFrom || !dateTo) return;
    setLoading(true);
    setError(null);
    // Clear stale data before new request → prevents flicker with old athlete
    setAggregate(null);
    setTimeseries(null);

    const params = { date_from: dateFrom, date_to: dateTo };
    if (teamId) params.team_id = teamId;

    Promise.all([
      api.get(`/reports/match-exposure/athlete/${athleteId}`, { params }),
      api.get(`/reports/match-exposure/athlete/${athleteId}/timeseries`, { params }),
    ])
      .then(([a, ts]) => {
        setAggregate(a.data || {});
        setTimeseries(ts.data || { series: [] });
      })
      .catch((e) => setError(e?.response?.data?.detail || t('Failed to load Match Exposure', 'Caricamento Match Exposure fallito')))
      .finally(() => setLoading(false));
  }, [athleteId, teamId, dateFrom, dateTo, api, t]);

  const filteredSeries = useMemo(() => {
    if (!timeseries?.series) return [];
    if (typeFilter === 'ALL') return timeseries.series;
    return timeseries.series.filter((r) => r.event_type === typeFilter);
  }, [timeseries, typeFilter]);

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="athlete-match-exposure-loading">
        <div className="flex items-center gap-2 text-zinc-400 text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('Loading…', 'Caricamento…')}
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="athlete-match-exposure-error">
        <div className="text-sm text-red-400 py-3">{error}</div>
      </Card>
    );
  }
  if (!aggregate) return null;

  const teamMatches = aggregate.team_matches ?? 0;
  const isEmpty = teamMatches === 0;
  const missing = aggregate.missing_exposure_entries ?? 0;
  const incomplete = aggregate.data_complete === false;

  return (
    <Card
      className="bg-zinc-900 border-zinc-800 p-5"
      data-testid="athlete-match-exposure-section"
    >
      <div className="flex items-start gap-3 mb-4">
        <Clock className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-zinc-100">
            {t('Match Exposure', 'Match Exposure')}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {t(
              'Actual individual playing time recorded per athlete.',
              'Minutaggio individuale effettivamente registrato per atleta.',
            )}
          </p>
        </div>
      </div>

      {isEmpty ? (
        <div
          className="text-xs text-zinc-500 italic py-6 text-center"
          data-testid="athlete-match-exposure-empty"
        >
          {t(
            'No Match Exposure data available for the selected period.',
            'Nessun dato Match Exposure disponibile nel periodo selezionato.',
          )}
        </div>
      ) : (
        <>
          {/* Data-quality warning (known totals may still show) */}
          {incomplete && (
            <div
              className="bg-amber-950/30 border border-amber-500/40 rounded p-2.5 mb-4 text-xs text-amber-200 flex items-start gap-2"
              data-testid="athlete-match-exposure-incomplete"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <b>
                  {t(
                    'Incomplete Match Exposure Data',
                    'Dati di minutaggio incompleti',
                  )}
                </b>
                {': '}
                {t(
                  `${missing} missing entries.`,
                  `${missing} rilevazioni mancanti.`,
                )}
              </div>
            </div>
          )}

          {/* Primary KPI cards */}
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3"
            data-testid="athlete-match-exposure-kpis-primary"
          >
            <KpiCard
              label={t('Minutes Played', 'Minuti giocati')}
              value={fmtNum(aggregate.minutes_played)}
              testid="ath-me-kpi-minutes"
              hint={`${t('of', 'di')} ${fmtNum(aggregate.total_available_match_minutes)} ${t('available', 'disponibili')}`}
            />
            <KpiCard
              label={t('Playing Time', 'Playing Time')}
              value={fmtPct(aggregate.playing_time_pct)}
              testid="ath-me-kpi-pct"
              hint={t('of team match minutes', 'sui minuti totali')}
            />
            <KpiCard
              label={t('Appearances', 'Presenze')}
              value={fmtNum(aggregate.appearances)}
              testid="ath-me-kpi-app"
              hint={`${t('over', 'su')} ${fmtNum(aggregate.team_matches)} ${t('team matches', 'partite squadra')}`}
            />
            <KpiCard
              label={t('Avg Minutes / Appearance', 'Media minuti / presenza')}
              value={fmtNum(aggregate.avg_minutes_per_appearance)}
              testid="ath-me-kpi-avgapp"
            />
            <KpiCard
              label={t('Average Match RPE', 'RPE medio partita')}
              value={fmtNum(aggregate.avg_rpe)}
              testid="ath-me-kpi-rpe"
              hint={
                isNum(aggregate.avg_rpe)
                  ? `${aggregate.rpe_entries}/${aggregate.matches_with_row} ${t('entered', 'inseriti')}`
                  : '—'
              }
            />
            <KpiCard
              label={t('Match-Day Load', 'Match-Day Load')}
              value={isNum(aggregate.match_day_training_load) ? `${aggregate.match_day_training_load} AU` : '—'}
              testid="ath-me-kpi-load"
            />
          </div>

          {/* Secondary compact strip */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-5"
            data-testid="athlete-match-exposure-kpis-secondary"
          >
            <div className="text-zinc-500">
              {t('Available Minutes', 'Minuti disponibili')}:{' '}
              <span className="text-zinc-200 font-medium">
                {fmtNum(aggregate.total_available_match_minutes)}
              </span>
            </div>
            <div className="text-zinc-500">
              {t('Team Matches', 'Partite squadra')}:{' '}
              <span className="text-zinc-200 font-medium">{fmtNum(aggregate.team_matches)}</span>
            </div>
            <div className="text-zinc-500">
              {t('Avg Minutes / Team Match', 'Media minuti / partita squadra')}:{' '}
              <span className="text-zinc-200 font-medium">
                {fmtNum(aggregate.avg_minutes_per_team_match)}
              </span>
            </div>
            <div className="text-zinc-500">
              {t('Post-Match Minutes', 'Minuti post partita')}:{' '}
              <span className="text-zinc-200 font-medium">{fmtNum(aggregate.post_match_minutes)}</span>
            </div>
            {missing > 0 && (
              <div className="text-amber-400 md:col-span-2" data-testid="ath-me-missing-badge">
                {t('Missing Data', 'Dati mancanti')}: {missing}
              </div>
            )}
          </div>

          {/* Chart tabs + type filter */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <ChartTabs
              value={chart}
              onChange={setChart}
              tabs={[
                { v: 'minutes', label: t('Minutes Played', 'Minuti giocati') },
                { v: 'playing', label: t('Playing Time', 'Playing Time') },
                { v: 'rpe', label: t('Match RPE', 'RPE partita') },
                { v: 'load', label: t('Match-Day Load', 'Match-Day Load') },
              ]}
            />
            <div className="flex items-center gap-2">
              <EventTypeTabs value={typeFilter} onChange={setTypeFilter} t={t} />
            </div>
          </div>

          {/* Filter/KPI decoupling notice — always visible, subtle */}
          {typeFilter !== 'ALL' && (
            <div
              className="text-[11px] text-zinc-500 italic flex items-center gap-1 mb-2"
              data-testid="ath-me-filter-notice"
            >
              <Info className="w-3 h-3" />
              {t(
                'Chart filtered. Summary KPI cards still reflect the full selected period.',
                'Grafico filtrato. Le KPI riepilogative restano sull\'intero periodo selezionato.',
              )}
            </div>
          )}

          {/* Chart body */}
          <div data-testid="athlete-match-exposure-chart">
            {filteredSeries.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center">
                {t(
                  'No matches in current filter.',
                  'Nessuna partita per il filtro selezionato.',
                )}
              </div>
            ) : (
              <ChartBody
                chart={chart}
                data={filteredSeries}
                t={t}
              />
            )}
          </div>

          {/* Legacy semantic hint (only if any friendly is missing exposure in filter=ALL) */}
          {missing > 0 && (
            <div
              className="mt-4 text-[11px] text-zinc-500 flex items-start gap-1.5"
              data-testid="ath-me-legacy-hint"
            >
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                {t(
                  'Training Distribution may include planned duration for legacy sessions. Match Exposure shows recorded individual playing time only.',
                  'Training Distribution può includere durata pianificata per sessioni storiche. Match Exposure mostra esclusivamente minutaggi individuali registrati.',
                )}
              </span>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

/* ─── Chart bodies ─────────────────────────────────────────── */
const ChartBody = ({ chart, data, t }) => {
  // Recharts: null values render as gaps automatically for line charts;
  // for bars we simply pass null (recharts skips them and NEVER renders 0).
  const rows = data.map((r) => ({
    ...r,
    label: r.date?.slice(5) || r.event_id.slice(0, 6),
  }));

  if (chart === 'minutes') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<AthleteMatchTooltip t={t} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            dataKey="match_minutes"
            name={t('Minutes Played', 'Minuti giocati')}
            fill="#1E6BFF"
          />
          <Line
            type="monotone"
            dataKey="match_available_minutes"
            name={t('Available Minutes', 'Minuti disponibili')}
            stroke="#F59E0B"
            strokeDasharray="4 3"
            dot={false}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (chart === 'playing') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis domain={[0, 100]} unit="%" tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<AthleteMatchTooltip t={t} />} />
          <ReferenceLine y={100} stroke="#334155" strokeDasharray="3 3" />
          <Line
            type="linear"
            dataKey="playing_time_pct"
            name={t('Playing Time %', 'Playing Time %')}
            stroke="#22C55E"
            strokeWidth={2}
            connectNulls={false}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (chart === 'rpe') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
          <Tooltip content={<AthleteMatchTooltip t={t} />} />
          <Line
            type="linear"
            dataKey="rpe"
            name={t('Match RPE', 'RPE partita')}
            stroke="#8B5CF6"
            strokeWidth={2}
            connectNulls={false}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  // load
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip content={<AthleteMatchTooltip t={t} />} />
        <Bar
          dataKey="training_load"
          name={t('Match-Day Load (AU)', 'Match-Day Load (AU)')}
          fill="#06B6D4"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default AthleteMatchExposureSection;
export { AthleteMatchExposureSection };
